import { ReactSession } from 'react-client-session';
import { useEffect, useMemo, useState } from 'react';
import { PriceChartApiResponse } from '../Charts';
import {
  Bot,
  Candle,
  LatestBotState,
  TransactionDetailsApiResponse,
} from '../types';
import TransactionCandles from './TransactionCandles';
import TransactionList from './TransactionList';
import axios from 'axios';
import { getTimeFrameIntervalTimestamp } from '../Charts/PriceChart/helpers';
import {
  CbrtRangeState,
  CurrentProfit,
  MrcState,
  SingleProfitCalculationApiResponse,
} from '../../ProfitCalculations/types';
import {
  fillTransactionIndexes,
  findTransactionWave,
  isCustomTransaction,
} from '../../../helpers/bundleTransactionsToCompactForm';
import { Transaction } from '../../Overview/types';
import { calculateStartOfWaveTimestamp } from '../../../helpers/calculateStartOfWaveTimestamp';
import './index.css';
import { CandleSticksLatestOrder } from '../Charts/types';

import ServicePaths from '../../../bridge/utils/ServicePaths';
import { TimeFrame } from '../../../types/Bot';

interface Props {
  bot: Bot;
  timeFrame: TimeFrame;
  candleSticksLatestOrder?: CandleSticksLatestOrder;
  transactions: TransactionDetailsApiResponse | undefined;
  isValidating: boolean;
}

const DetailTransaction = (props: Props) => {
  const {
    bot,
    timeFrame,
    candleSticksLatestOrder,
    transactions,
    isValidating,
  } = props;

  const [candles, setCandles] = useState<PriceChartApiResponse | undefined>(
    undefined,
  );
  const [isCompareToProfitCalcs, setIsCompareToProfitCalcs] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [multiOrderCandles, setMultiOrderCandles] = useState<
    PriceChartApiResponse | undefined
  >();
  const [shorterTCandles, setShorterTCandles] = useState<
    PriceChartApiResponse | undefined
  >();

  const [profitCalcsTransactions, setProfitCalcsTransactions] = useState<
    Transaction[] | undefined
  >();
  const [compactProfitCalcsTransactions, setCompactProfitCalcsTransactions] =
    useState<Transaction[] | undefined>([]);
  const [profitCalcsCurrentProfit, setProfitCalcsCurrentProfit] = useState<
    CurrentProfit | undefined
  >();

  const [latestBotStateProfitData, setLatestBotStateProfitData] = useState<
    Array<LatestBotState> | undefined
  >();

  const realCompactTransactions = useMemo(() => {
    if (!transactions?.compactTransactions?.length) {
      return [];
    }

    return transactions.compactTransactions
      .filter(
        (t) =>
          !isCustomTransaction(t) ||
          !profitCalcsTransactions?.length ||
          !isCompareToProfitCalcs,
      )
      .filter(
        (t) =>
          !isCompareToProfitCalcs ||
          !compactProfitCalcsTransactions?.length ||
          compactProfitCalcsTransactions[0]?.transactionTimestamp <=
            t.transactionTimestamp,
      );
  }, [
    compactProfitCalcsTransactions,
    isCompareToProfitCalcs,
    profitCalcsTransactions?.length,
    transactions?.compactTransactions,
  ]);

  const bundledTransactions = useMemo(() => {
    if (!realCompactTransactions) {
      return {};
    }

    const bundledTransactions: Record<string, Transaction[]> = {};

    const allTransactions = realCompactTransactions.concat(
      isCompareToProfitCalcs ? compactProfitCalcsTransactions ?? [] : [],
    );

    for (let i = 0; i < allTransactions.length; i++) {
      const isRealTransaction = i < realCompactTransactions.length;
      const transaction = {
        ...allTransactions[i],
        type: isRealTransaction ? 'real' : 'profit',
      };
      const transactionTimestamp = transaction.transactionTimestamp;

      /** We need the multiOrder candles now */
      const candle = multiOrderCandles?.normal.find(
        (candle) =>
          candle.startTimestamp <= transactionTimestamp &&
          transactionTimestamp <= candle.endTimestamp,
      );
      const startTimestamp = candle?.startTimestamp.toString();

      if (!startTimestamp) {
        continue;
      }

      if (typeof bundledTransactions[startTimestamp] === 'undefined') {
        bundledTransactions[startTimestamp] = [transaction];
        continue;
      } else {
        bundledTransactions[startTimestamp].push(transaction);
      }
    }

    return bundledTransactions;
  }, [
    compactProfitCalcsTransactions,
    isCompareToProfitCalcs,
    multiOrderCandles?.normal,
    realCompactTransactions,
  ]);

  const currentComplexParams = useMemo(() => {
    if (!bot.complexParams) {
      return undefined;
    }

    return {
      complexParams: bot.complexParams,
      timeFrames: bot.timeFrame,
    };
  }, [bot.complexParams, bot.timeFrame]);

  const complexParamsBundlesByTimestamp = useMemo(() => {
    if (
      currentComplexParams === undefined ||
      !realCompactTransactions?.length
    ) {
      return [];
    }

    const complexParamsBundlesByTimestamp = [];

    /**
     * Each compact transaction
     * Compare to latestComplexParamsBundle
     * If different
     * Save the latestComplexParamsBundle with the startDate of the endTimestamp of the compactTransaction
     * Continue
     * If the compact transaction does not have complexParamsBundle
     * Add the latestComplexParamsBundle and break
     * If last one, push also
     */

    let currentcomplexParamsBundle = currentComplexParams;
    let endTimestamp = new Date().valueOf();

    for (let i = 0; i < realCompactTransactions.length; i++) {
      const compactTransaction = realCompactTransactions[i];

      if (!compactTransaction.complexParams || !compactTransaction.timeFrames) {
        complexParamsBundlesByTimestamp.push({
          complexParamsBundle: currentcomplexParamsBundle,
          startTimestamp: compactTransaction.transactionTimestamp,
          latestOrder: compactTransaction.orderType,
          endTimestamp,
        });
        break;
      }

      const complexParamsBundle = {
        complexParams: compactTransaction.complexParams,
        timeFrames: compactTransaction.timeFrames,
      };

      const hasSameComplexParamsBundle = true;
      // const hasSameComplexParamsBundle = compareComplexParamsBundle(
      //   complexParamsBundle,
      //   compactTransaction,
      // );

      if (!hasSameComplexParamsBundle) {
        complexParamsBundlesByTimestamp.push({
          complexParamsBundle: complexParamsBundle,
          startTimestamp: compactTransaction.transactionTimestamp,
          endTimestamp,
          latestOrder: compactTransaction.orderType,
        });

        currentcomplexParamsBundle = complexParamsBundle;
        endTimestamp = compactTransaction.transactionTimestamp;
      }
    }

    return complexParamsBundlesByTimestamp;
  }, [currentComplexParams, realCompactTransactions]);

  /** Fetch candles */
  useEffect(() => {
    const fetch = async () => {
      if (!bot) {
        return;
      }

      setIsLoading(true);

      const { baseAsset, quoteAsset } = bot;

      const firstTransaction =
        transactions?.compactTransactions[
          transactions?.compactTransactions.length - 1
        ];
      const endTimestamp = new Date().valueOf();

      if (!firstTransaction) {
        return;
      }

      const { data } = await axios({
        url: ServicePaths.CANDLE_FETCHER.FetchHeikinCandles,
        params: {
          baseAsset,
          quoteAsset,
          timeFrame: bot.timeFrame.higher,
          startTimestamp:
            firstTransaction.transactionTimestamp -
            getTimeFrameIntervalTimestamp(bot.timeFrame.higher),
          endTimestamp,
          includeLatestCandle: true,
          exchange: bot.defaultExchange,
        },
      });

      setCandles(data);
      setIsLoading(false);
    };

    if (!bot) {
      return;
    }

    fetch();
  }, [bot, transactions?.compactTransactions]);

  /**
   * Fetch multiOrder candles
   */
  useEffect(() => {
    const fetchMultiOrderTCandles = async () => {
      const firstTransaction =
        transactions?.compactTransactions[
          transactions?.compactTransactions.length - 1
        ];

      const { data } = await axios({
        url: ServicePaths.CANDLE_FETCHER.FetchHeikinCandles,
        params: {
          baseAsset: bot.baseAsset,
          quoteAsset: bot.quoteAsset,
          timeFrame: bot.timeFrame.multiOrder,
          startTimestamp: firstTransaction?.transactionTimestamp
            ? firstTransaction.transactionTimestamp -
              getTimeFrameIntervalTimestamp(bot.timeFrame.multiOrder)
            : new Date().valueOf(),
          endTimestamp: new Date().valueOf(),
          includeLatestCandle: true,
          exchange: bot.defaultExchange,
        },
      });

      setMultiOrderCandles(data);
    };

    const fetchShorterTCandles = async () => {
      const firstTransaction =
        transactions?.compactTransactions[
          transactions?.compactTransactions.length - 1
        ];

      const { data } = await axios({
        url: ServicePaths.CANDLE_FETCHER.FetchHeikinCandles,
        params: {
          baseAsset: bot.baseAsset,
          quoteAsset: bot.quoteAsset,
          timeFrame: bot.timeFrame.shorter,
          startTimestamp: firstTransaction?.transactionTimestamp
            ? firstTransaction.transactionTimestamp -
              getTimeFrameIntervalTimestamp(bot.timeFrame.shorter)
            : new Date().valueOf(),
          endTimestamp: new Date().valueOf(),
          includeLatestCandle: true,
          exchange: bot.defaultExchange,
        },
      });

      setShorterTCandles(data);
    };

    fetchMultiOrderTCandles();
    fetchShorterTCandles();
  }, [
    bot.baseAsset,
    bot.defaultExchange,
    bot.quoteAsset,
    bot.timeFrame.multiOrder,
    bot.timeFrame.shorter,
    transactions?.allTransactions,
    transactions?.compactTransactions,
  ]);

  useEffect(() => {
    if (
      isLoading ||
      profitCalcsTransactions ||
      !bot.complexParams ||
      !candles?.heikin ||
      !isCompareToProfitCalcs ||
      !complexParamsBundlesByTimestamp?.length
    ) {
      return;
    }

    /** We want to calculate current complex param transactions */

    const fetchTransactions = async () => {
      setIsLoading(true);

      for (const complexParamsBundleByTimestamp of complexParamsBundlesByTimestamp) {
        const params = {
          baseAsset: bot.baseAsset,
          quoteAsset: bot.quoteAsset,
          exchange: bot.defaultExchange,
          startTimestamp: calculateStartOfWaveTimestamp(
            complexParamsBundleByTimestamp.startTimestamp,
            candles.heikin,
            undefined,
          ),
          endTimestamp: complexParamsBundleByTimestamp.endTimestamp,
          higherTimeFrame:
            complexParamsBundleByTimestamp.complexParamsBundle.timeFrames
              .higher,
          shorterTimeFrame:
            complexParamsBundleByTimestamp.complexParamsBundle.timeFrames
              .shorter,
          multiOrderTimeFrame:
            complexParamsBundleByTimestamp.complexParamsBundle.timeFrames
              .multiOrder,
          cbrt: complexParamsBundleByTimestamp.complexParamsBundle.complexParams
            .cbrt,
          shorterTimeFrameInterval:
            complexParamsBundleByTimestamp.complexParamsBundle.complexParams
              .shorterTimeFrameInterval,
          supportCbrtRange:
            complexParamsBundleByTimestamp.complexParamsBundle.complexParams
              .supportCbrtRange,
          supportMrcState:
            complexParamsBundleByTimestamp.complexParamsBundle.complexParams
              .supportMrcState,
          latestOrder: complexParamsBundleByTimestamp.latestOrder,
        };

        const { data }: { data: SingleProfitCalculationApiResponse } =
          await axios({
            method: 'POST',
            url: ServicePaths.PROFIT_CALCULATIONS.GetSingleProfitCalculation,
            data: params,
          });

        // const [pr, compact] = fillTransactionIndexes(
        //   formatTransactionType(data.transactions, bot),
        //   formatTransactionType(
        //     bundleTransactionsToCompactForm(data.transactions),
        //     bot,
        //   ),
        // );

        // setProfitCalcsTransactions((prev) =>
        //   [...(prev ?? []), ...pr].sort(
        //     (a, b) => a.transactionTimestamp - b.transactionTimestamp,
        //   ),
        // );
        // setCompactProfitCalcsTransactions((prev) =>
        //   [...(prev ?? []), ...compact].sort(
        //     (a, b) => a.transactionTimestamp - b.transactionTimestamp,
        //   ),
        // );

        if (!profitCalcsCurrentProfit) {
          setProfitCalcsCurrentProfit({
            currentProfit: data.currentProfit,
            currentPriceChange: data.currentPriceChange,
          });
        }

        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, [
    bot,
    candles?.heikin,
    complexParamsBundlesByTimestamp,
    isCompareToProfitCalcs,
    isLoading,
    profitCalcsCurrentProfit,
    profitCalcsTransactions,
  ]);

  const compactTransactionWithoutCustomTransactions = useMemo(() => {
    return (
      transactions?.compactTransactions
        .filter((t) => !isCustomTransaction(t))
        .sort((a, b) => b.transactionTimestamp - a.transactionTimestamp) || []
    );
  }, [transactions?.compactTransactions]);

  useEffect(() => {
    if (!transactions) {
      return transactions;
    }

    fillTransactionIndexes(
      transactions.allTransactions,
      transactions?.compactTransactions,
    );

    if (
      !candles?.heikin ||
      !shorterTCandles?.heikin.length ||
      !multiOrderCandles?.heikin.length
    ) {
      return;
    }

    /** Fill candles */
    for (const transaction of [
      ...transactions.allTransactions,
      ...transactions?.compactTransactions,
    ]) {
      /**
       * De trade in het midden
       * Links en rechts de vorige en volgende trade (compact)
       */

      let startTimestamp, endTimestamp;
      const compactTransactionId = transaction.compactTransactionId;

      const compactTransactionIndex =
        compactTransactionWithoutCustomTransactions.findIndex(
          (t) => t.compactTransactionId === compactTransactionId,
        );

      if (compactTransactionIndex === 0) {
        endTimestamp = transaction.transactionTimestamp;
      } else {
        const nextCompactTransaction =
          compactTransactionWithoutCustomTransactions[
            compactTransactionIndex - 1
          ];

        if (!!nextCompactTransaction) {
          endTimestamp = nextCompactTransaction.transactionTimestamp;
        } else {
          endTimestamp = new Date().valueOf();
        }
      }

      if (
        compactTransactionIndex ===
        compactTransactionWithoutCustomTransactions.length - 1
      ) {
        startTimestamp = transaction.transactionTimestamp;
      } else {
        const previousCompactTransaction =
          compactTransactionWithoutCustomTransactions[
            compactTransactionIndex + 1
          ];
        startTimestamp = previousCompactTransaction.transactionTimestamp;
      }

      const waveStartTimstamp = calculateStartOfWaveTimestamp(
        transaction.transactionTimestamp,
        candles?.heikin,
        transaction.orderType,
      );

      const higherTransactionWave = findTransactionWave(
        candles?.heikin,
        startTimestamp,
        endTimestamp,
      );

      const start = !!higherTransactionWave?.length
        ? higherTransactionWave[0].startTimestamp
        : startTimestamp;
      const end = !!higherTransactionWave?.length
        ? higherTransactionWave[higherTransactionWave.length - 1].endTimestamp
        : endTimestamp;

      transaction.candles = {
        higher: findTransactionWave(
          candles?.heikin,
          startTimestamp,
          endTimestamp,
        ),
        shorter: shorterTCandles.heikin.filter(
          (c) => start <= c.startTimestamp && c.endTimestamp <= end,
        ),
        multi: multiOrderCandles?.heikin.filter(
          (c) =>
            waveStartTimstamp <= c.startTimestamp &&
            c.startTimestamp <=
              transaction.transactionTimestamp +
                5 * (c.endTimestamp - c.startTimestamp),
        ),
      };
    }
  }, [
    candles,
    compactTransactionWithoutCustomTransactions,
    multiOrderCandles?.heikin,
    shorterTCandles?.heikin,
    transactions,
  ]);

  /**
   * @TODO
   * We need to fetch multiple profit calcs huhuuu
   *
   * Start timestamp = latest transaction
   * End timestamp = now
   *
   * Fetch the complex params archive
   * The first one needs to start 1 before the start timestamp
   *
   * For each record,
   * we'll have to do a profit calcs
   *
   * Do this in one data call
   */

  useEffect(() => {
    if (
      !!latestBotStateProfitData
      // latestBotStateProfitData.baseAsset === bot.baseAsset
    ) {
      return;
    }

    const fetch = async () => {
      if (
        !transactions?.allTransactions.length ||
        !bot.complexParams ||
        !bot.timeFrame
      ) {
        return;
      }

      /** To have the cbrt range states and mrc states, we need to do a profit calc */
      /**
       * We should start at the latest transaction
       * But filter the candles from latest wave
       */

      const params = {
        botId: bot.botId,
      };

      const { data }: { data: Array<LatestBotState> } = await axios({
        method: 'POST',
        url: ServicePaths.DATA.GetClientLatestBotState,
        data: params,
      });

      setLatestBotStateProfitData(data);
    };

    fetch();
  }, [
    bot.baseAsset,
    bot.botId,
    bot.complexParams,
    bot.defaultExchange,
    bot.quoteAsset,
    bot.timeFrame,
    latestBotStateProfitData,
    transactions?.allTransactions,
  ]);

  const latestBotState = useMemo(() => {
    /** Combine all the arrays in 1 here */

    if (!latestBotStateProfitData?.length) {
      return undefined;
    }

    const lastBotState =
      latestBotStateProfitData[latestBotStateProfitData.length - 1];

    const candles = latestBotStateProfitData
      .reduce((acc, curr) => [...acc, ...curr.candles], [] as Candle[])
      .filter(
        (c, i, s) =>
          s.findIndex((sc) => sc.startTimestamp === c.startTimestamp) === i,
      );
    const wakeUpTimes = latestBotStateProfitData.reduce(
      (acc, curr) => [...acc, ...curr.wakeUpTimes],
      [] as number[],
    );
    const cbrtRangeStates = latestBotStateProfitData.reduce(
      (acc, curr) => [...acc, ...curr.cbrtRangeStates],
      [] as CbrtRangeState[],
    );
    const mrcStates = latestBotStateProfitData.reduce(
      (acc, curr) => [...acc, ...curr.mrcStates],
      [] as MrcState[],
    );
    const transactions = latestBotStateProfitData.reduce(
      (acc, curr) => [...acc, ...curr.transactions],
      [] as any[],
    );

    return {
      orderType: lastBotState.orderType,
      currentProfit: lastBotState.currentProfit,
      currentPriceChange: lastBotState.currentPriceChange,
      candles,
      wakeUpTimes,
      cbrtRangeStates,
      mrcStates,
      transactions,
      bot: lastBotState.bot,
      baseAsset: lastBotState.baseAsset,
      quoteAsset: lastBotState.quoteAsset,
      timeFrame: lastBotState.timeFrame,
    } as LatestBotState;
  }, [latestBotStateProfitData]);

  const complexParamsChangeTimestamps = useMemo(
    () =>
      latestBotStateProfitData
        ? [...latestBotStateProfitData]
            .slice(1)
            .map((s) => s.startTimestamp || 0)
        : [],
    [latestBotStateProfitData],
  );

  return (
    <div className="detail-transactions-root" key={bot.botId}>
      <TransactionCandles
        bot={bot}
        timeFrame={timeFrame}
        candles={candles}
        transactions={transactions}
        isLoading={isLoading}
        multiOrderCandles={multiOrderCandles}
        bundledTransactions={bundledTransactions}
        realCompactTransactions={realCompactTransactions}
        isCompareToProfitCalcs={isCompareToProfitCalcs}
        setIsCompareToProfitCalcs={setIsCompareToProfitCalcs}
        compactProfitCalcsTransactions={compactProfitCalcsTransactions}
        profitCalcsTransactions={profitCalcsTransactions}
        setIsLoading={setIsLoading}
        candleSticksLatestOrder={candleSticksLatestOrder}
      />
      <TransactionList
        transactions={transactions}
        isLoading={isValidating}
        compactTransactionLength={transactions?.compactTransactions.length}
        currentComplexParams={currentComplexParams}
        isDoubleView={
          isCompareToProfitCalcs && !!compactProfitCalcsTransactions?.length
        }
        transactionsByTimestamp={bundledTransactions}
        profitCalcsTransactions={profitCalcsTransactions}
        profitCalcsCurrentProfit={profitCalcsCurrentProfit}
        latestBotState={latestBotState}
        bot={bot}
        complexParamsChangeTimestamps={complexParamsChangeTimestamps}
      />
    </div>
  );
};

export default DetailTransaction;
